import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar } from 'expo-status-bar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import * as Linking from 'expo-linking';
import { useTranslation } from 'react-i18next';
import HarmoniaLogo from '../components/HarmoniaLogo';

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE =
  'https://eueke282zksk1zki18susjdksisk18sj.onrender.com/auth';

type MessageType = 'success' | 'error' | 'info' | 'warning';

interface StatusMessage {
  type: MessageType;
  text: string;
  visible: boolean;
}

// ─── COMPOSANT ────────────────────────────────────────────────────────────────
// Cet écran est atteint depuis le lien de reset dans l'email Supabase.
//
// Web :
// https://www.harmoniaworld.world/reset#access_token=xxx&type=recovery
//
// Mobile :
// Le lien HTTPS passe par Android App Links puis Expo Router.
// Nous récupérons l'URL reçue avec expo-linking.
//
// Le token utilisé ensuite par le backend Render est le
// access_token Supabase.

export default function ResetScreen() {
  const router = useRouter();
  const { t } = useTranslation();

  const [token, setToken] = useState('');
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [statusMessage, setStatusMessage] = useState<StatusMessage>({
    type: 'info',
    text: '',
    visible: false,
  });

  // ─── EXTRACTION DU TOKEN DEPUIS L'URL ─────────────────────────────────────
  //
  // Supabase redirige normalement vers :
  //
  // https://www.harmoniaworld.world/reset
  // #access_token=xxx
  // &refresh_token=xxx
  // &type=recovery
  //
  // En cas d'erreur :
  //
  // https://www.harmoniaworld.world/reset
  // #error=access_denied
  // &error_code=otp_expired
  // &error_description=...
  //
  // Sur Web : window.location.href
  //
  // Sur Android/iOS :
  // Linking.getInitialURL()
  // + Linking.addEventListener('url', ...)

  useEffect(() => {
    let mounted = true;

    const extractTokenFromURL = (url: string | null) => {
      try {
        if (!url) {
          console.warn('[reset] Aucune URL reçue.');
          if (mounted) {
            setToken('');
            setTokenLoaded(true);
          }
          return;
        }

        console.log('[reset] URL reçue :', url);

        // ─────────────────────────────────────────────────────────────
        // 1. Récupération du fragment #
        // ─────────────────────────────────────────────────────────────
        const hashIndex = url.indexOf('#');

        const hash =
          hashIndex !== -1
            ? url.substring(hashIndex + 1)
            : '';

        // ─────────────────────────────────────────────────────────────
        // 2. Récupération éventuelle de la query ?
        // ─────────────────────────────────────────────────────────────
        const questionIndex = url.indexOf('?');

        const query =
          questionIndex !== -1
            ? url
                .substring(questionIndex + 1)
                .split('#')[0]
            : '';

        const hashParams = new URLSearchParams(hash);
        const queryParams = new URLSearchParams(query);

        // ─────────────────────────────────────────────────────────────
        // 3. Récupération des paramètres Supabase
        // ─────────────────────────────────────────────────────────────
        const accessToken =
          hashParams.get('access_token') ||
          queryParams.get('access_token');

        const refreshToken =
          hashParams.get('refresh_token') ||
          queryParams.get('refresh_token');

        const type =
          hashParams.get('type') ||
          queryParams.get('type');

        const error =
          hashParams.get('error') ||
          queryParams.get('error');

        const errorCode =
          hashParams.get('error_code') ||
          queryParams.get('error_code');

        const errorDescription =
          hashParams.get('error_description') ||
          queryParams.get('error_description');

        console.log('[reset] type =', type);
        console.log(
          '[reset] access_token présent =',
          !!accessToken
        );
        console.log(
          '[reset] refresh_token présent =',
          !!refreshToken
        );
        console.log('[reset] error =', error);
        console.log('[reset] error_code =', errorCode);

        // ─────────────────────────────────────────────────────────────
        // 4. Supabase a renvoyé une erreur
        // ─────────────────────────────────────────────────────────────
        //
        // Exemple :
        // #error=access_denied
        // &error_code=otp_expired
        //
        // Dans ce cas, le problème intervient avant le formulaire.
        // Le backend Render n'est pas encore appelé.

        if (error || errorCode) {
          console.error(
            '[reset] Erreur Supabase :',
            errorCode || error,
            errorDescription || ''
          );

          if (mounted) {
            setToken('');

            setStatusMessage({
              type: 'error',
              text:
                errorDescription ||
                t('login.reset.confirm.invalidLink.subtitle'),
              visible: true,
            });

            setTokenLoaded(true);
          }

          return;
        }

        // ─────────────────────────────────────────────────────────────
        // 5. Token de récupération valide
        // ─────────────────────────────────────────────────────────────

        if (accessToken && type === 'recovery') {
          console.log(
            '[reset] Token recovery trouvé ✅'
          );

          if (mounted) {
            setToken(accessToken);
            setTokenLoaded(true);
          }

          return;
        }

        // ─────────────────────────────────────────────────────────────
        // 6. Aucun token valide
        // ─────────────────────────────────────────────────────────────

        console.warn(
          '[reset] Aucun access_token recovery trouvé.'
        );

        if (mounted) {
          setToken('');
          setTokenLoaded(true);
        }
      } catch (err) {
        console.error(
          '[reset] extractTokenFromURL:',
          err
        );

        if (mounted) {
          setToken('');
          setTokenLoaded(true);
        }
      }
    };

    // ─────────────────────────────────────────────────────────────────
    // WEB
    // ─────────────────────────────────────────────────────────────────
    if (Platform.OS === 'web') {
      try {
        if (typeof window !== 'undefined') {
          const currentUrl = window.location.href;

          console.log(
            '[reset] URL Web :',
            currentUrl
          );

          extractTokenFromURL(currentUrl);
        } else {
          setTokenLoaded(true);
        }
      } catch (err) {
        console.error(
          '[reset] erreur URL Web :',
          err
        );

        if (mounted) {
          setTokenLoaded(true);
        }
      }

      return () => {
        mounted = false;
      };
    }

    // ─────────────────────────────────────────────────────────────────
    // MOBILE
    // ─────────────────────────────────────────────────────────────────

    // Cas 1 :
    // L'application était fermée et est lancée par le lien.
    Linking.getInitialURL()
      .then((initialUrl) => {
        console.log(
          '[reset] Initial URL mobile :',
          initialUrl
        );

        if (mounted) {
          extractTokenFromURL(initialUrl);
        }
      })
      .catch((err) => {
        console.error(
          '[reset] getInitialURL:',
          err
        );

        if (mounted) {
          setToken('');
          setTokenLoaded(true);
        }
      });

    // Cas 2 :
    // L'application était déjà ouverte lorsque l'utilisateur
    // clique sur le lien de récupération.
    const subscription = Linking.addEventListener(
      'url',
      ({ url }) => {
        console.log(
          '[reset] Nouveau lien mobile :',
          url
        );

        if (mounted) {
          extractTokenFromURL(url);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.remove();
    };
  }, [t]);

  // ─── AUTO-HIDE STATUS APRÈS 5S ─────────────────────────────────────────────

  useEffect(() => {
    if (!statusMessage.visible) return;

    const timeout = setTimeout(() => {
      setStatusMessage((previous) => ({
        ...previous,
        visible: false,
      }));
    }, 5000);

    return () => clearTimeout(timeout);
  }, [statusMessage.visible]);

  // ─── MESSAGE ──────────────────────────────────────────────────────────────

  const showMessage = (
    type: MessageType,
    text: string
  ) => {
    if (Platform.OS !== 'web') {
      Haptics.notificationAsync(
        type === 'error'
          ? Haptics.NotificationFeedbackType.Error
          : type === 'success'
          ? Haptics.NotificationFeedbackType.Success
          : Haptics.NotificationFeedbackType.Warning
      );
    }

    setStatusMessage({
      type,
      text,
      visible: true,
    });
  };

  // ─── RÉINITIALISATION MOT DE PASSE ────────────────────────────────────────

  const handleResetPassword = async () => {
    if (!token) {
      showMessage(
        'error',
        t('login.reset.confirm.invalidLink.subtitle')
      );
      return;
    }

    if (
      !newPassword.trim() ||
      !confirmPassword.trim()
    ) {
      showMessage(
        'error',
        t('login.reset.confirm.messages.fillBothFields')
      );
      return;
    }

    if (newPassword.length < 6) {
      showMessage(
        'error',
        t(
          'login.reset.confirm.messages.passwordTooShort'
        )
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showMessage(
        'error',
        t(
          'login.reset.confirm.messages.passwordsDoNotMatch'
        )
      );
      return;
    }

    setLoading(true);

    showMessage(
      'info',
      t('login.reset.confirm.messages.resetting')
    );

    try {
      const response = await fetch(API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'reset-password',
          token,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ||
            t(
              'login.reset.confirm.messages.resetError'
            )
        );
      }

      // Le backend Render retourne une nouvelle session
      // après modification du mot de passe.
      if (data.session) {
        await AsyncStorage.setItem(
          'harmonia_session',
          JSON.stringify(data.session)
        );
      }

      showMessage(
        'success',
        t(
          'login.reset.confirm.messages.passwordChanged'
        )
      );

      setTimeout(() => {
  router.replace('/(tabs)/actu');
}, 1500);
    } catch (error: any) {
      console.error(
        '[reset] handleResetPassword:',
        error
      );

      showMessage(
        'error',
        error?.message ||
          t(
            'login.reset.confirm.messages.cannotResetPassword'
          )
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── RENDU ────────────────────────────────────────────────────────────────

  return (
    <KeyboardAvoidingView
      behavior={
        Platform.OS === 'ios'
          ? 'padding'
          : 'height'
      }
      style={styles.container}
    >
      <StatusBar style="light" />

      <LinearGradient
        colors={['#8A2BE2', '#4B0082']}
        style={styles.gradientBackground}
      >
        {/* MESSAGE DE STATUT */}
        {statusMessage.visible && (
          <View
            style={[
              styles.statusMessageContainer,
              styles[
                `statusMessage${
                  statusMessage.type
                    .charAt(0)
                    .toUpperCase() +
                  statusMessage.type.slice(1)
                }` as keyof typeof styles
              ],
            ]}
          >
            <Ionicons
              name={
                statusMessage.type === 'success'
                  ? 'checkmark-circle'
                  : statusMessage.type === 'error'
                  ? 'close-circle'
                  : statusMessage.type === 'warning'
                  ? 'warning'
                  : 'information-circle'
              }
              size={24}
              color="#fff"
              style={styles.statusIcon}
            />

            <Text style={styles.statusMessageText}>
              {statusMessage.text}
            </Text>
          </View>
        )}

        {/* LOADING OVERLAY */}
        {loading && (
          <View style={styles.loadingOverlay}>
            <View style={styles.loadingCard}>
              <ActivityIndicator
                size="large"
                color="#8A2BE2"
              />

              <Text style={styles.loadingText}>
                {t('login.common.loading')}
              </Text>
            </View>
          </View>
        )}

        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.logoContainer}>
            <HarmoniaLogo
              size={80}
              showText={true}
              theme="light"
            />

            <Text style={styles.tagline}>
              {t('login.common.tagline')}
            </Text>
          </View>

          <View style={styles.card}>
            {/* CHARGEMENT DU TOKEN */}
            {!tokenLoaded && (
              <>
                <ActivityIndicator
                  size="large"
                  color="#8A2BE2"
                  style={{
                    marginVertical: 40,
                  }}
                />

                <Text style={styles.subtitle}>
                  {t(
                    'login.reset.confirm.checkingLink'
                  )}
                </Text>
              </>
            )}

            {/* TOKEN ABSENT OU INVALIDE */}
            {tokenLoaded && !token && (
              <>
                <Ionicons
                  name="close-circle-outline"
                  size={60}
                  color="#EF4444"
                  style={{
                    alignSelf: 'center',
                    marginBottom: 20,
                  }}
                />

                <Text style={styles.title}>
                  {t(
                    'login.reset.confirm.invalidLink.title'
                  )}
                </Text>

                <Text style={styles.subtitle}>
                  {statusMessage.visible &&
                  statusMessage.type === 'error'
                    ? statusMessage.text
                    : t(
                        'login.reset.confirm.invalidLink.subtitle'
                      )}
                </Text>

                <TouchableOpacity
                  onPress={() =>
                    router.replace('/login')
                  }
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[
                      '#FF0080',
                      '#FF8C00',
                    ]}
                    style={styles.primaryButton}
                  >
                    <Text style={styles.buttonText}>
                      {t(
                        'login.reset.confirm.backToLogin'
                      )}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              </>
            )}

            {/* FORMULAIRE NOUVEAU MOT DE PASSE */}
            {tokenLoaded && token && (
              <>
                <Ionicons
                  name="lock-open-outline"
                  size={60}
                  color="#8A2BE2"
                  style={{
                    alignSelf: 'center',
                    marginBottom: 20,
                  }}
                />

                <Text style={styles.title}>
                  {t(
                    'login.reset.confirm.title'
                  )}
                </Text>

                <Text style={styles.subtitle}>
                  {t(
                    'login.reset.confirm.subtitle'
                  )}
                </Text>

                {/* NOUVEAU MOT DE PASSE */}
                <View style={styles.inputContainer}>
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color="#999"
                    style={styles.inputIcon}
                  />

                  <TextInput
                    style={styles.input}
                    placeholder={t(
                      'login.reset.confirm.newPasswordPlaceholder'
                    )}
                    placeholderTextColor="#999"
                    value={newPassword}
                    onChangeText={setNewPassword}
                    secureTextEntry={!showNew}
                    autoCapitalize="none"
                    editable={!loading}
                  />

                  <TouchableOpacity
                    onPress={() =>
                      setShowNew(!showNew)
                    }
                    disabled={loading}
                  >
                    <Ionicons
                      name={
                        showNew
                          ? 'eye-outline'
                          : 'eye-off-outline'
                      }
                      size={20}
                      color="#999"
                    />
                  </TouchableOpacity>
                </View>

                {/* CONFIRMATION MOT DE PASSE */}
                <View style={styles.inputContainer}>
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color="#999"
                    style={styles.inputIcon}
                  />

                  <TextInput
                    style={styles.input}
                    placeholder={t(
                      'login.reset.confirm.confirmPasswordPlaceholder'
                    )}
                    placeholderTextColor="#999"
                    value={confirmPassword}
                    onChangeText={setConfirmPassword}
                    secureTextEntry={!showConfirm}
                    autoCapitalize="none"
                    editable={!loading}
                  />

                  <TouchableOpacity
                    onPress={() =>
                      setShowConfirm(!showConfirm)
                    }
                    disabled={loading}
                  >
                    <Ionicons
                      name={
                        showConfirm
                          ? 'eye-outline'
                          : 'eye-off-outline'
                      }
                      size={20}
                      color="#999"
                    />
                  </TouchableOpacity>
                </View>

                {/* BOUTON RESET */}
                <TouchableOpacity
                  onPress={handleResetPassword}
                  disabled={loading}
                  activeOpacity={0.8}
                >
                  <LinearGradient
                    colors={[
                      '#11998e',
                      '#38ef7d',
                    ]}
                    style={styles.primaryButton}
                  >
                    <Text style={styles.buttonText}>
                      {t(
                        'login.reset.confirm.submit'
                      )}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>

                {/* RETOUR LOGIN */}
                <TouchableOpacity
                  onPress={() =>
                    router.replace('/login')
                  }
                  style={styles.linkButton}
                  disabled={loading}
                >
                  <Text style={styles.linkText}>
                    <Text style={styles.linkBold}>
                      {t(
                        'login.reset.confirm.backToLoginArrow'
                      )}
                    </Text>
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </ScrollView>
      </LinearGradient>
    </KeyboardAvoidingView>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  gradientBackground: {
    flex: 1,
  },

  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },

  logoContainer: {
    alignItems: 'center',
    marginBottom: 40,
  },

  tagline: {
    fontSize: 16,
    color: '#E0D0FF',
    fontStyle: 'italic',
    marginTop: 12,
    letterSpacing: 1,
  },

  card: {
    backgroundColor: '#fff',
    borderRadius: 25,
    padding: 30,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 10,
    },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    elevation: 10,
  },

  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
    textAlign: 'center',
  },

  subtitle: {
    fontSize: 14,
    color: '#666',
    marginBottom: 30,
    textAlign: 'center',
    lineHeight: 20,
  },

  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    borderRadius: 12,
    paddingHorizontal: 15,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },

  inputIcon: {
    marginRight: 10,
  },

  input: {
    flex: 1,
    paddingVertical: 15,
    fontSize: 16,
    color: '#333',
  },

  primaryButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 10,
  },

  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },

  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },

  linkText: {
    color: '#666',
    fontSize: 14,
  },

  linkBold: {
    color: '#8A2BE2',
    fontWeight: 'bold',
  },

  statusMessageContainer: {
    position: 'absolute',
    top: 50,
    left: 20,
    right: 20,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 12,
    zIndex: 1000,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },

  statusMessageSuccess: {
    backgroundColor: '#10B981',
  },

  statusMessageError: {
    backgroundColor: '#EF4444',
  },

  statusMessageWarning: {
    backgroundColor: '#F59E0B',
  },

  statusMessageInfo: {
    backgroundColor: '#3B82F6',
  },

  statusIcon: {
    marginRight: 10,
  },

  statusMessageText: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },

  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999,
  },

  loadingCard: {
    backgroundColor: '#fff',
    padding: 30,
    borderRadius: 20,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },

  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: '#333',
    fontWeight: '600',
  },
});
